// Trigger Vercel deployment - Force update
import React, { useState } from 'react';
import { Eye, EyeOff, Info, Check } from 'lucide-react';
import { LLM_PROVIDERS, type LLMConfiguration, type LLMProvider, type LLMModel } from '../config/llmProviders';
import { useUser } from '../contexts/UserContext';
import { UserModelsService } from '../services/userModelsService';
import { ActiveModelsList } from './ActiveModelsList';
import { useToast } from '../contexts/ToastContext';
import { PremiumDropdown } from './ui/PremiumDropdown';

import {
    OpenAIIcon,
    AnthropicIcon,
    MistralIcon,
    GoogleIcon,
    CohereIcon,
    MetaIcon,
    AzureIcon,
    PerplexityIcon,
    AI21Icon,
    HuggingFaceIcon,
} from './icons/ProviderIcons';

interface LLMSettingsProps {
    currentConfig: LLMConfiguration;
    onConfigChange: (config: LLMConfiguration) => void;
}

export function LLMSettings({ currentConfig, onConfigChange }: LLMSettingsProps) {
    const { user } = useUser();
    const [selectedProvider, setSelectedProvider] = useState<LLMProvider | undefined>(
        LLM_PROVIDERS.find(p => p.id === currentConfig.providerId)
    );
    const [selectedModel, setSelectedModel] = useState<LLMModel | undefined>(
        selectedProvider?.models.find(m => m.id === currentConfig.modelId)
    );
    const [apiKey, setApiKey] = useState(currentConfig.apiKey || '');
    const [parameters, setParameters] = useState(currentConfig.parameters || {});
    const [showApiKey, setShowApiKey] = useState(false);
    const { showToast } = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const userModelsService = new UserModelsService();

    const handleProviderChange = (providerId: string) => {
        const provider = LLM_PROVIDERS.find(p => p.id === providerId);
        if (provider) {
            setSelectedProvider(provider);

            // Tenta selecionar o modelo "auto" se existir, senão usa o defaultModel
            const autoModel = provider.models.find(m => m.id === 'auto');
            const modelToSelect = autoModel || provider.models.find(m => m.id === provider.defaultModel) || provider.models[0];
            setSelectedModel(modelToSelect);

            setApiKey('');
            setParameters(
                provider.parameters.reduce((acc, param) => ({
                    ...acc,
                    [param.id]: param.default
                }), {})
            );
        }
    };

    const handleModelChange = (modelId: string) => {
        if (selectedProvider) {
            const model = selectedProvider.models.find(m => m.id === modelId);
            setSelectedModel(model);
        }
    };

    const handleParameterChange = (key: string, value: number | boolean | string) => {
        setParameters(prev => ({
            ...prev,
            [key]: value
        }));
    };




    // Validação de formato da API key
    const validateApiKeyFormat = (key: string, providerId: string): { valid: boolean; message?: string } => {
        if (!key || key.trim() === '') {
            return { valid: false, message: 'A chave API não pode estar vazia' };
        }

        const patterns: Record<string, { regex: RegExp; example: string }> = {
            openai: {
                regex: /^sk-(proj-)?[A-Za-z0-9_-]{20,}$/,
                example: 'sk-proj-...'
            },
            anthropic: {
                regex: /^sk-ant-[A-Za-z0-9-]{20,}$/,
                example: 'sk-ant-...'
            },
            google: {
                regex: /^AIza[A-Za-z0-9_-]{35}$/,
                example: 'AIza...'
            }
        };

        const pattern = patterns[providerId];
        if (!pattern) {
            // Provider sem validação específica
            return { valid: true };
        }

        if (!pattern.regex.test(key)) {
            return {
                valid: false,
                message: `Formato inválido. Esperado: ${pattern.example}`
            };
        }

        return { valid: true };
    };

    const handleSaveConfiguration = async () => {
        // Validação de formato se houver API key
        if (apiKey && apiKey.trim() !== '' && selectedProvider) {
            const validation = validateApiKeyFormat(apiKey, selectedProvider.id);
            if (!validation.valid) {
                showToast('error', validation.message || 'Formato de API key inválido');
                return;
            }
        }

        setIsSaving(true);

        try {
            // Handle model activation/deactivation
            if (user && selectedProvider) {
                const supportedProviders = ['openai', 'anthropic', 'google'];

                if (supportedProviders.includes(selectedProvider.id)) {
                    const providerId = selectedProvider.id as 'openai' | 'anthropic' | 'google';

                    // If API key is empty or being cleared, deactivate models
                    if (!apiKey || apiKey.trim() === '') {
                        await userModelsService.deactivateProviderModels(user.id, providerId);
                        console.log(`[LLMSettings] Deactivated models for ${providerId}`);
                    }
                    // If API key is provided, activate models
                    else {
                        await userModelsService.activateProviderModels(
                            user.id,
                            providerId,
                            apiKey
                        );
                        console.log(`[LLMSettings] Activated models for ${providerId}`);
                    }
                }
            }

            await onConfigChange({
                providerId: selectedProvider?.id || currentConfig.providerId,
                modelId: selectedModel?.id || currentConfig.modelId,
                apiKey,
                parameters
            });
            showToast('success', 'Suas preferências de IA foram atualizadas e salvas com segurança no banco de dados.', {
                title: 'Configurações Salvas'
            });

            // Reset connection status after successful save
            setConnectionStatus('idle');
        } catch (error: unknown) {
            console.error('Error saving configuration:', error);
            showToast('error', 'Erro ao salvar configurações LLM');

        } finally {
            setIsSaving(false);
        }
    };



    const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
        openai: OpenAIIcon,
        anthropic: AnthropicIcon,
        mistral: MistralIcon,
        google: GoogleIcon,
        cohere: CohereIcon,
        meta: MetaIcon,
        azure: AzureIcon,
        perplexity: PerplexityIcon,
        ai21: AI21Icon,
        huggingface: HuggingFaceIcon,
    };

    return (
        <div className="space-y-6 relative">
            {/* Header with Save Button */}
            <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Configuração LLM</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Gerencie seus provedores e modelos</p>
                </div>

                <div className="flex items-center gap-3">
                    {/* Status Messages in Header - Removed in favor of Toast */}

                    <button
                        onClick={handleSaveConfiguration}
                        disabled={isSaving || !selectedProvider || (selectedModel?.id !== 'auto' && connectionStatus !== 'success')}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center gap-2"
                        title={selectedModel?.id === 'auto' ? 'Salvar configurações' : (connectionStatus !== 'success' ? 'Teste a conexão antes de salvar' : 'Salvar configurações')}
                    >
                        {isSaving ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Check className="w-4 h-4" />
                        )}
                        <span>Salvar</span>
                    </button>
                </div>
            </div>

            {/* Provider Selection - Cascading Dropdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PremiumDropdown
                    label="Provedor"
                    value={selectedProvider?.id}
                    onChange={handleProviderChange}
                    options={LLM_PROVIDERS.map(provider => ({
                        value: provider.id,
                        label: provider.id === 'auto' ? 'Automático' : provider.displayName,
                        icon: iconMap[provider.icon],
                        badge: provider.id === 'auto' ? 'RECOMENDADO' : undefined
                    }))}
                    placeholder="Selecione um provedor"
                    searchable={true}
                />

                <PremiumDropdown
                    label="Modelo"
                    value={selectedModel?.id}
                    onChange={handleModelChange}
                    options={selectedProvider ? selectedProvider.models.map(model => ({
                        value: model.id,
                        label: model.displayName,
                        badge: model.id === 'auto' ? 'RECOMENDADO' : undefined
                    })) : []}
                    placeholder={selectedProvider ? 'Selecione um modelo' : 'Selecione um provedor primeiro'}
                    searchable={true}
                    disabled={!selectedProvider}
                />
            </div>

            {
                selectedProvider && selectedModel && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Model Info Card */}
                        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-800">
                            <div className="flex flex-wrap gap-4 justify-between items-start">
                                <div className="space-y-1">
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                        {selectedModel.displayName}
                                        <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-bold uppercase tracking-wider">
                                            {selectedModel.contextWindow.toLocaleString()} tokens
                                        </span>
                                    </h4>
                                    {selectedModel.pricing && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            ${selectedModel.pricing.input}/1M in · ${selectedModel.pricing.output}/1M out
                                        </p>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    {selectedModel.capabilities.vision && (
                                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded text-xs font-medium border border-purple-200 dark:border-purple-800/30 flex items-center gap-1">
                                            👁️ Vision
                                        </span>
                                    )}
                                    {selectedModel.capabilities.functionCalling && (
                                        <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded text-xs font-medium border border-blue-200 dark:border-blue-800/30 flex items-center gap-1">
                                            🔧 Tools
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* API Key - Simplified */}
                        {selectedProvider.requiresApiKey && (
                            <div className="space-y-3">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {selectedProvider.apiKeyLabel}
                                </label>

                                {/* API Key Input with Gradient Border */}
                                <div className="relative p-[1px] rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
                                    <div className="relative bg-white dark:bg-[#030712] rounded-full">
                                        <input
                                            type={showApiKey ? 'text' : 'password'}
                                            value={apiKey}
                                            onChange={e => setApiKey(e.target.value)}
                                            placeholder={selectedProvider.apiKeyPlaceholder}
                                            className="w-full pl-4 pr-12 py-3 bg-transparent border-0 rounded-full focus:outline-none text-gray-900 dark:text-white text-sm placeholder-gray-500"
                                            autoComplete="new-password"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                            data-1p-ignore
                                            data-lpignore="true"
                                            data-form-type="other"
                                        />
                                        <button
                                            onClick={() => setShowApiKey(!showApiKey)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md text-gray-500 transition-colors"
                                            type="button"
                                        >
                                            {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>


                                {/* Help Messages */}
                                <div className="space-y-2">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                        <Info className="w-3 h-3 flex-shrink-0" />
                                        <span>Sua chave será criptografada e armazenada com segurança no banco de dados.</span>
                                    </p>
                                    <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                        <Info className="w-3 h-3 flex-shrink-0" />
                                        <span>A chave será validada automaticamente quando você usar o agente pela primeira vez.</span>
                                    </p>
                                    {selectedProvider.id === 'openai' && (
                                        <a
                                            href="https://platform.openai.com/api-keys"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 underline inline-flex items-center gap-1"
                                        >
                                            Obter chave API da OpenAI →
                                        </a>
                                    )}
                                    {selectedProvider.id === 'anthropic' && (
                                        <a
                                            href="https://console.anthropic.com/settings/keys"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 underline inline-flex items-center gap-1"
                                        >
                                            Obter chave API da Anthropic →
                                        </a>
                                    )}
                                    {selectedProvider.id === 'google' && (
                                        <a
                                            href="https://aistudio.google.com/app/apikey"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 underline inline-flex items-center gap-1"
                                        >
                                            Obter chave API do Google →
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Parameters - Enhanced Sliders */}
                        <div className="space-y-6 pt-2">
                            <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-800">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Parâmetros</h3>
                            </div>

                            <div className="grid gap-6">
                                {selectedProvider.parameters.map(param => (
                                    <div key={param.id} className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                                {param.displayName}
                                                <div className="group relative">
                                                    <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                                                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                                                        {param.description}
                                                    </div>
                                                </div>
                                            </label>
                                            {param.type === 'number' && (
                                                <span className="text-xs font-mono font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded">
                                                    {parameters[param.id] !== undefined ? String(parameters[param.id]) : param.default}
                                                </span>
                                            )}
                                        </div>

                                        {param.type === 'number' && (
                                            <div className="relative h-6 flex items-center">
                                                <input
                                                    type="range"
                                                    min={param.min}
                                                    max={param.max}
                                                    step={param.step}
                                                    value={parameters[param.id] !== undefined ? Number(parameters[param.id]) : Number(param.default)}
                                                    onChange={e => handleParameterChange(param.id, parseFloat(e.target.value))}
                                                    className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600 hover:accent-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                                    style={{
                                                        backgroundImage: `linear-gradient(to right, #3b82f6 0%, #8b5cf6 100%)`,
                                                        backgroundSize: `${((Number(parameters[param.id] ?? param.default) - (param.min ?? 0)) / ((param.max ?? 1) - (param.min ?? 0))) * 100}% 100%`,
                                                        backgroundRepeat: 'no-repeat'
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {param.type === 'boolean' && (
                                            <label className="flex items-center gap-3 cursor-pointer group">
                                                <div className="relative">
                                                    <input
                                                        type="checkbox"
                                                        checked={parameters[param.id] !== undefined ? Boolean(parameters[param.id]) : Boolean(param.default)}
                                                        onChange={e => handleParameterChange(param.id, e.target.checked)}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                                </div>
                                                <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200 transition-colors">
                                                    {param.description}
                                                </span>
                                            </label>
                                        )}

                                        {param.type === 'select' && param.options && (
                                            <select
                                                value={parameters[param.id] !== undefined ? String(parameters[param.id]) : String(param.default)}
                                                onChange={e => handleParameterChange(param.id, e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900 dark:text-white"
                                            >
                                                {param.options.map(opt => (
                                                    <option key={String(opt.value)} value={String(opt.value)}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )
            }

            <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                <ActiveModelsList />
            </div>
        </div>

    );
}
